import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Grid, IconButton, TextField, Typography } from "@mui/material";
import useStyles from "./style";
import { useFormik } from "formik";
import * as yup from "yup";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import settingsApi from "Services/Settings";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import moment from "moment";

const normalizeValue = (value) => (value ?? "").toString().trim();

const profileFieldKeys = [
  "singleton",
  "company_name",
  "registration_number",
  "headquarters_address",
  "legal_representative_name",
  "legal_representative_title",
  "claim_period",
  "_id",
  "id",
  "metuaaProfileId",
  "profileId",
];

const isProfileLikeObject = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  profileFieldKeys.some((key) =>
    Object.prototype.hasOwnProperty.call(value, key)
  );

const extractProfileRecord = (response) => {
  const candidates = [
    response?.data?.data?.data,
    response?.data?.data,
    response?.data?.record,
    response?.data?.item,
    response?.data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      const firstRecord = candidate[0];
      if (isProfileLikeObject(firstRecord)) {
        return firstRecord;
      }
    }

    if (isProfileLikeObject(candidate)) {
      return candidate;
    }
  }

  return null;
};

const getProfileId = (profile) =>
  profile?._id || profile?.id || profile?.metuaaProfileId || profile?.profileId || null;

const isSuccessResponse = (response) =>
  response?.status >= 200 &&
  response?.status < 300 &&
  response?.data?.status !== false;

const formatDateTime = (value) =>
  value ? moment(value).format("DD MMM YYYY, HH:mm") : "--";

const ContractSettings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [contractProfile, setContractProfile] = useState(null);

  const profileId = useMemo(() => getProfileId(contractProfile), [contractProfile]);
  const isEditMode = Boolean(profileId);

  const loadContractProfile = useCallback(
    async ({ showGlobalLoader = true } = {}) => {
      if (showGlobalLoader) {
        dispatch(showLoader("Loading please wait..."));
      }

      try {
        const response = await settingsApi.getMetuaaProfile();
        const record = extractProfileRecord(response);
        setContractProfile(record);
        return record;
      } catch (error) {
        const status = error?.response?.status;

        if (status === 404 || status === 204) {
          setContractProfile(null);
          return null;
        }

        dispatch(
          showSnackbar({
            message:
              error?.response?.data?.message || "Failed to fetch contract settings",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
        return null;
      } finally {
        if (showGlobalLoader) {
          dispatch(hideLoader());
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    loadContractProfile();
  }, [loadContractProfile]);

  const initialValues = useMemo(
    () => ({
      company_name: normalizeValue(contractProfile?.company_name),
      registration_number: normalizeValue(contractProfile?.registration_number),
      headquarters_address: normalizeValue(contractProfile?.headquarters_address),
      legal_representative_name: normalizeValue(
        contractProfile?.legal_representative_name
      ),
      legal_representative_title: normalizeValue(
        contractProfile?.legal_representative_title
      ),
      claim_period: contractProfile?.claim_period ?? "",
    }),
    [contractProfile]
  );

  const handleSaveContractSettings = useCallback(
    async (formValues) => {
      const payload = {
        singleton: 1,
        company_name: normalizeValue(formValues?.company_name),
        registration_number: normalizeValue(formValues?.registration_number),
        headquarters_address: normalizeValue(formValues?.headquarters_address),
        legal_representative_name: normalizeValue(
          formValues?.legal_representative_name
        ),
        legal_representative_title: normalizeValue(
          formValues?.legal_representative_title
        ),
        claim_period: Number(formValues?.claim_period),
      };

      dispatch(showLoader("Loading please wait..."));

      try {
        const response = isEditMode
          ? await settingsApi.updateMetuaaProfile(profileId, payload)
          : await settingsApi.createMetuaaProfile(payload);

        if (!isSuccessResponse(response)) {
          throw new Error("Unexpected contract settings response");
        }

        dispatch(
          showSnackbar({
            message:
              response?.data?.message ||
              (isEditMode
                ? "Contract settings updated successfully"
                : "Contract settings created successfully"),
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );

        const savedRecord = extractProfileRecord(response);

        if (savedRecord) {
          setContractProfile(savedRecord);
        } else {
          await loadContractProfile({ showGlobalLoader: false });
        }
      } catch (error) {
        dispatch(
          showSnackbar({
            message:
              error?.response?.data?.message ||
              error?.message ||
              `Error while ${isEditMode ? "updating" : "creating"} contract settings`,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, isEditMode, loadContractProfile, profileId]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      company_name: yup.string().trim().required("Company name is required"),
      registration_number: yup
        .string()
        .trim()
        .required("Registration number is required"),
      headquarters_address: yup
        .string()
        .trim()
        .required("Headquarters address is required"),
      legal_representative_name: yup
        .string()
        .trim()
        .required("Legal representative name is required"),
      legal_representative_title: yup
        .string()
        .trim()
        .required("Legal representative title is required"),
      claim_period: yup
        .number()
        .typeError("Claim period must be a number")
        .integer("Claim period must be an integer")
        .min(0, "Claim period must be greater than or equal to 0")
        .required("Claim period is required"),
    }),
    onSubmit: handleSaveContractSettings,
  });

  const updatedAtLabel = formatDateTime(
    contractProfile?.updated_at || contractProfile?.updatedAt
  );

  return (
    <Grid container className={classes.cardContainer}>
      <Grid item xs={12} className={classes.businessSettingsContainer}>
        <Box className={classes.headerRow}>
          <Box className={classes.headerLeft}>
            <Box className={classes.headerIconBadge}>
              <DescriptionOutlined />
            </Box>
            <Box className={classes.titleBlock}>
              <Typography variant="h5" className={classes.pageTitle}>
                Contract Settings
              </Typography>
              <Typography variant="body2" className={classes.pageSubtitle}>
                Configure the singleton metuaa-profile used by contract flows.
              </Typography>
            </Box>
          </Box>

          <Box className={classes.headerActions}>
            <Chip
              label={isEditMode ? "Existing record loaded" : "Ready to create"}
              variant="outlined"
              color={isEditMode ? "success" : "default"}
              className={classes.statusChip}
            />
            <IconButton
              aria-label="refresh"
              onClick={() => loadContractProfile()}
            >
              <RefreshRoundedIcon />
            </IconButton>
            <Button
              variant="contained"
              onClick={formik.handleSubmit}
              className={classes.primaryButton}
            >
              {isEditMode ? "Update Contract Settings" : "Create Contract Settings"}
            </Button>
          </Box>
        </Box>

        <Box className={classes.statsRow}>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>Statut du profil</Typography>
            <Typography className={classes.statValue}>
              {isEditMode ? "Configuré" : "Non configuré"}
            </Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>Dernière mise à jour</Typography>
            <Typography className={classes.statValue}>{updatedAtLabel}</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>Période de réclamation</Typography>
            <Typography className={classes.statValue}>
              {typeof contractProfile?.claim_period === "number"
                ? `${contractProfile.claim_period} jour(s)`
                : "--"}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} className={classes.formGrid}>
          <Grid item xs={12} md={6} className={classes.fieldWrapper}>
            <TextField
              name="company_name"
              id="company_name"
              label="Company Name"
              value={formik.values.company_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.company_name && formik.errors.company_name
              )}
              helperText={
                formik.touched.company_name && formik.errors.company_name
              }
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6} className={classes.fieldWrapper}>
            <TextField
              name="registration_number"
              id="registration_number"
              label="Registration Number"
              value={formik.values.registration_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.registration_number &&
                  formik.errors.registration_number
              )}
              helperText={
                formik.touched.registration_number &&
                formik.errors.registration_number
              }
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} className={classes.fieldWrapper}>
            <TextField
              name="headquarters_address"
              id="headquarters_address"
              label="Headquarters Address"
              value={formik.values.headquarters_address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.headquarters_address &&
                  formik.errors.headquarters_address
              )}
              helperText={
                formik.touched.headquarters_address &&
                formik.errors.headquarters_address
              }
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6} className={classes.fieldWrapper}>
            <TextField
              name="legal_representative_name"
              id="legal_representative_name"
              label="Legal Representative Name"
              value={formik.values.legal_representative_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.legal_representative_name &&
                  formik.errors.legal_representative_name
              )}
              helperText={
                formik.touched.legal_representative_name &&
                formik.errors.legal_representative_name
              }
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6} className={classes.fieldWrapper}>
            <TextField
              name="legal_representative_title"
              id="legal_representative_title"
              label="Legal Representative Title"
              value={formik.values.legal_representative_title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.legal_representative_title &&
                  formik.errors.legal_representative_title
              )}
              helperText={
                formik.touched.legal_representative_title &&
                formik.errors.legal_representative_title
              }
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4} className={classes.fieldWrapper}>
            <TextField
              name="claim_period"
              id="claim_period"
              label="Claim Period"
              type="number"
              value={formik.values.claim_period}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.claim_period && formik.errors.claim_period)}
              helperText={formik.touched.claim_period && formik.errors.claim_period}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{
                min: 0,
                step: 1,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ContractSettings;
